import CryptoJS from "crypto-js";
import { BlobReader, BlobWriter, TextWriter, ZipReader } from "@zip.js/zip.js";
import axios from "../../config/axiosConfig";
import { getCloudFrontSourcePath } from "../../utils/functions";
import {
	GAMES_CACHE,
	OFFLINE_SDK_FILES,
	akedoFileUrl,
	contentTypeFor,
	findGameRoot,
	gameFileUrl,
	isBrotliFile,
	isGzipFile,
	isPatchableText,
	patchTextFile,
	relativeGamePath,
	userIdFromToken,
} from "./gameFiles";
import { getOfflineGame, removeOfflineGameEntry, saveOfflineGame } from "./registry";
import { bootstrapOfflineProgress, registerOfflineServiceWorker, removeOfflineGameFiles } from "./serviceWorker";

// Same CDN the web player loads games from (GamePlayIFrameComponent).
const GAME_CDN = "https://dcklsnvoejo0x.cloudfront.net";
const DOWNLOAD_SHARE = 0.8; // progress: 0–80% download, 80–100% unpack

// Games the server allows offline. Their download keys are encrypted for the user
// the server saw on this request (also in the response), which can differ from the
// current token if the list loaded before login finished, so that id is kept with them.
export const fetchDownloadableGames = async () => {
	const { data } = await axios.get("api/downloadable_bundle");
	const dkeyUserId = data?.user?.id ? String(data.user.id) : "";
	return (data?.apps || []).filter((game) => game?.id && game?.download && game?.dkey).map((game) => ({ ...game, dkeyUserId }));
};

// The zip password is encrypted per user (CryptoJS AES keyed by sha256(userId)), as in the app.
const decryptZipPassword = (dkey, userId) => {
	let password = "";
	try {
		password = CryptoJS.AES.decrypt(dkey, CryptoJS.SHA256(userId).toString()).toString(CryptoJS.enc.Utf8);
	} catch (_) {
		// A wrong key can produce invalid UTF-8.
	}
	if (!password) throw new Error("Couldn't unlock this game's download. Refresh the page and try again.");
	return password;
};

const downloadZip = async (url, onProgress, signal) => {
	const response = await fetch(url, { signal });
	if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
	const total = Number(response.headers.get("content-length")) || 0;
	if (!response.body || !total) return response.blob();

	const reader = response.body.getReader();
	const chunks = [];
	let received = 0;
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
		received += value.length;
		onProgress?.((received / total) * DOWNLOAD_SHARE);
	}
	return new Blob(chunks);
};

const gunzip = (blob) => new Response(blob.stream().pipeThrough(new DecompressionStream("gzip"))).blob();

const putFile = (cache, url, body, contentType) =>
	cache.put(new Request(url), new Response(body, { headers: { "Content-Type": contentType } }));

const fetchForCache = async (url, init) => {
	const response = await fetch(url, init);
	if (!response.ok && response.type !== "opaque") throw new Error(`Couldn't fetch ${url} (HTTP ${response.status})`);
	return response;
};

// Stores the game's files in the games cache (served by public/sw.js).
const storeGameFiles = async (game, zip, { onProgress, signal }) => {
	const password = decryptZipPassword(game.dkey, game.dkeyUserId ?? userIdFromToken(localStorage.getItem("token")));
	const reader = new ZipReader(new BlobReader(zip), { password, signal });
	const cache = await caches.open(GAMES_CACHE);
	try {
		const entries = (await reader.getEntries()).filter((entry) => !entry.directory);
		const root = findGameRoot(entries.map((entry) => entry.filename));
		if (root === null) throw new Error("This game's download has no index.html");
		const files = entries
			.map((entry) => ({ entry, path: relativeGamePath(entry.filename, root) }))
			.filter((file) => file.path);
		if (files.some((file) => isBrotliFile(file.path))) throw new Error("This game can't be played offline in the browser yet");

		let done = 0;
		for (const { entry, path } of files) {
			signal?.throwIfAborted();
			const servedPath = isGzipFile(path) ? path.replace(/\.gz$/i, "") : path;
			const url = gameFileUrl(game.id, path);
			const contentType = contentTypeFor(path);
			if (isPatchableText(path)) {
				const text = await entry.getData(new TextWriter());
				await putFile(cache, url, patchTextFile(path, text, game.id), contentType);
			} else {
				const blob = await entry.getData(new BlobWriter());
				const body = isGzipFile(path) ? await gunzip(blob) : blob;
				await putFile(cache, url, body, contentType);
				// Unity requests the file by its .gz name, other loaders by the plain name.
				if (servedPath !== path) await putFile(cache, gameFileUrl(game.id, servedPath), body, contentType);
			}
			done += 1;
			onProgress?.(DOWNLOAD_SHARE + (done / files.length) * (1 - DOWNLOAD_SHARE) * 0.95);
		}
	} finally {
		await reader.close();
	}

	// The SDK and the icon go in the game folder too, so the game and its row work offline.
	for (const { fileName, source } of OFFLINE_SDK_FILES) {
		await cache.put(new Request(akedoFileUrl(game.id, fileName)), await fetchForCache(source, { signal }));
	}
	if (!game.icon) return "";
	const source = `${getCloudFrontSourcePath()}${game.icon}`;
	const iconUrl = akedoFileUrl(game.id, "icon");
	try {
		await cache.put(new Request(iconUrl), await fetchForCache(source, { mode: "cors", signal }));
	} catch (error) {
		if (signal?.aborted) throw error;
		// The CDN sometimes answers without CORS headers (a copy cached from a
		// plain <img> load). An opaque copy still displays fine in an <img>.
		try {
			await cache.put(new Request(iconUrl), await fetchForCache(source, { mode: "no-cors", signal }));
		} catch (_error) {
			return "";
		}
	}
	return iconUrl;
};

// Downloads a game the user has already played and stores it for offline play.
// `game` comes from fetchDownloadableGames(). The game only counts as downloaded
// (saved to the registry) once everything succeeded, including the player's
// server progress, so offline play never starts from scratch over it. A failed
// or cancelled download leaves nothing behind.
export const downloadOfflineGame = async (game, { onProgress, signal } = {}) => {
	await registerOfflineServiceWorker();
	if (navigator.storage?.persist) navigator.storage.persist().catch(() => {});
	const userToken = localStorage.getItem("token") || "";

	try {
		const zip = await downloadZip(`${GAME_CDN}${game.download}`, onProgress, signal);
		signal?.throwIfAborted();
		const iconUrl = await storeGameFiles(game, zip, { onProgress, signal });
		signal?.throwIfAborted();
		// The game was played, so this initjs session doesn't charge an entry fee.
		if (userToken) await bootstrapOfflineProgress(game.id, userToken);
		signal?.throwIfAborted();

		saveOfflineGame({
			id: game.id,
			name: game.name,
			developer: game.developer,
			categoryId: game.categoryId,
			isPortrait: Boolean(game.isPortrait),
			icon: iconUrl,
			download: game.download,
			downloadedAt: Date.now(),
		});
		onProgress?.(1);
		return getOfflineGame(game.id);
	} catch (error) {
		await removeOfflineGameFiles(game.id).catch(() => {});
		throw error;
	}
};

export const removeOfflineGame = async (appId) => {
	const result = await removeOfflineGameFiles(appId);
	removeOfflineGameEntry(appId);
	return result;
};
